// ---------------------------------------------------------------------------
// LOCAL DEV MOCK MODE
// ---------------------------------------------------------------------------
// When enabled, intercepts all API calls on localhost and returns fixture data,
// and auto-authenticates so the OTP/SMS step is skipped entirely. This lets the
// whole flow (login -> appointments -> booking) be developed and screenshotted
// without a backend and without sending real SMS.
//
// Activation (dev machine only): create a `.env.local` file with:
//     REACT_APP_DEV_MOCK=1
// `.env.local` is git-ignored by Create React App, so this NEVER ships to
// production. On any real subdomain `REACT_APP_DEV_MOCK` is unset -> mock off.
// ---------------------------------------------------------------------------

import axios from "axios";

const DEV_TOKEN = "dev-mock-token";

// The mock is OFF for normal browsing (so you always see your REAL data).
// It activates ONLY when explicitly requested with `?mock=1` in the URL, and
// the choice is remembered (use `?mock=0` to turn it back off).
const isLocal = /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname);
const params = new URLSearchParams(window.location.search);
if (params.has("mock")) {
  if (params.get("mock") === "0") localStorage.removeItem("DEV_MOCK");
  else localStorage.setItem("DEV_MOCK", "1");
}

export const DEV_MOCK =
  process.env.REACT_APP_DEV_MOCK === "1" &&
  isLocal &&
  localStorage.getItem("DEV_MOCK") === "1";

// --- fixtures --------------------------------------------------------------

const MOCK_DEPARTMENTS = [
  { id: 1, title: "גביה ושירות לקוחות" },
  { id: 2, title: "הנדסה" },
  { id: 3, title: "אכיפה" },
  { id: 4, title: "גביה מחלקת הנדסה" },
];

// Several slots across days in June 2026 (today is mocked around 2026-06-09).
const daySlots = (wdate, times) =>
  times.map((t, i) => ({
    id: Number(wdate.replace(/-/g, "")) * 100 + i,
    wdate,
    start_time: t,
    slot_config_id: 1,
  }));

const SCHEDULE_SLOTS = [
  ...daySlots("2026-06-14", [
    "09:00:00", "09:20:00", "09:40:00", "10:00:00", "10:20:00", "10:40:00",
    "11:00:00", "11:20:00", "11:40:00", "12:00:00", "12:20:00", "12:40:00",
    "13:00:00", "13:20:00", "13:40:00", "14:00:00", "14:20:00", "14:40:00",
    "15:00:00", "15:20:00", "15:40:00",
  ]),
  ...daySlots("2026-06-15", ["09:00:00", "09:20:00", "09:40:00", "10:00:00"]),
  ...daySlots("2026-06-16", ["11:00:00", "11:30:00", "12:00:00"]),
  ...daySlots("2026-06-21", ["13:00:00", "13:30:00"]),
  ...daySlots("2026-06-22", ["08:30:00", "09:00:00", "09:30:00", "10:00:00"]),
  ...daySlots("2026-06-28", ["15:00:00", "15:30:00", "16:00:00"]),
  // August 2026 is a 6-row calendar month — used to repro the time-list-over-CTA bug.
  // A long morning list so the scroll window + chevron are exercised.
  ...["2026-08-02", "2026-08-03", "2026-08-04", "2026-08-05", "2026-08-06",
      "2026-08-09", "2026-08-10", "2026-08-11", "2026-08-12", "2026-08-13",
      "2026-08-30", "2026-08-31"].flatMap((d) =>
    daySlots(d, [
      "07:00:00", "07:10:00", "07:20:00", "07:30:00", "07:40:00", "07:50:00",
      "08:00:00", "08:10:00", "08:20:00", "08:30:00", "08:40:00", "08:50:00",
      "09:00:00", "09:10:00", "09:20:00", "09:30:00",
    ])
  ),
];

const MOCK_SCHEDULES = [
  {
    id: 11,
    title: "כללי",
    address: "המייסדים 41, קומה 2, אבן יהודה",
    slots: SCHEDULE_SLOTS,
  },
  {
    id: 12,
    title: "סניף מרכז",
    address: "ויצמן 10, כפר סבא",
    slots: daySlots("2026-06-17", ["10:00:00", "10:30:00", "11:00:00"]),
  },
];

const MOCK_USER = {
  id: 1,
  mobile: "0501234567",
  fname: "אלירן",
  lname: "כהן",
  departments: MOCK_DEPARTMENTS,
  visitors: [
    {
      id: 1,
      appointed_time: "2026-08-30 08:00:00",
      department_id: 1,
      schedule_id: 11,
      fname: "אלירן",
      lname: "כהן",
      id_number: "040142689",
      mobile: "0501234567",
      mynote: "יש להגיע 10 דקות לפני מועד הפגישה ולהצטייד בתעודת זהות ובכל המסמכים הנדרשים לטיפול. במידה ואינך יכול/ה להגיע, נא לבטל את התור מבעוד מועד כדי לפנות את התור לאחרים. חניה זמינה בחניון הסמוך בתשלום.",
      schedule: { title: "כללי", address: "המייסדים 41, קומה 2, אבן יהודה" },
      // ‏C2b — כך השרת מוסר את השדות שסומנו "כרטיס התור של האזרח": תווית
      // וערך כבר מפורמטים (כן/לא, תאריך), כדי שהפרונט לא ישכפל את הכללים.
      extra_display: [{ label: "מספר נפשות", value: "4" }],
    },
    {
      id: 2,
      appointed_time: "2026-05-20 10:30:00",
      department_id: 2,
      schedule_id: 12,
      fname: "אלירן",
      lname: "כהן",
      schedule: { title: "סניף מרכז", address: "ויצמן 10, כפר סבא" },
      // ‏C2b — תור-עבר **בלי** שדות נוספים: מדמה תור שנקבע לפני שהלקוח הגדיר
      // אותם. "קבע תור חוזר" עליו חייב להחזיר את האזרח לשלב-הפרטים כדי שימלא
      // שדה-חובה, במקום להיכשל בשליחה.
    },
    {
      id: 3,
      appointed_time: "2026-05-11 09:15:00",
      department_id: 2,
      schedule_id: 12,
      fname: "אלירן",
      lname: "כהן",
      schedule: { title: "סניף מרכז", address: "ויצמן 10, כפר סבא" },
      // ‏C2b — תור-עבר **עם** שדות נוספים: "קבע תור חוזר" עליו מעביר את
      // הערכים ולכן מדלג ישר לבחירת השירות, כמו לפני הפיצ'ר.
      extra_fields: { nefashot: "5" },
      extra_display: [{ label: "מספר נפשות", value: "5" }],
    },
  ],
};

// --- request matching ------------------------------------------------------

function buildMockBody(config) {
  const url = (config.url || "").toLowerCase();
  const method = (config.method || "get").toLowerCase();

  /* ★★ `user/site-config` **אינו מוקאפ** — הוא עובר לבקנד האמיתי.
     מצב-המוק נועד לעקוף **אימות ונתוני-תורים**, לא את ההגדרות של הלקוח.
     כשהוא כן היה ממוקאפ, השדות-הנוספים (C2b) שהוגדרו בניהול פשוט לא הופיעו
     במצב-דמו — מה שנראה כמו פיצ'ר שבור, וגם הפך כל בדיקה אוטומטית במצב-מוק
     לחסרת-ערך (היא בדקה את הפיקסצ'ר, לא את המערכת).
     ‏הפעולה ציבורית וללא אימות, ו-`fetchSiteConfig` כבר עוטף ב-try/catch
     ומחזיר {} — כך שגם בלי בקנד זמין הטופס ממשיך לעבוד. */
  if (url.includes("user/emergency")) return { data: { emergence_mode: 0, emergency_text: "" } };
  if (url.includes("user/send-otp")) return { data: "otp-sent" };
  if (url.includes("user/login")) return { data: { token: DEV_TOKEN } };
  if (url.includes("user/me")) return { data: MOCK_USER };
  if (url.includes("doctors")) return { data: { rows: MOCK_SCHEDULES } };
  if (url.includes("visitors") && method === "post")
    return { data: { id: 999, schedule: MOCK_SCHEDULES[0], mynote: "" } };
  if (url.includes("visitors")) return { data: { success: true } };
  if (url.match(/\/user\/?$/)) return { data: MOCK_USER };
  return null;
}

export function installDevMock() {
  if (!DEV_MOCK) {
    // Clean up a leftover mock token so real browsing isn't affected.
    if (localStorage.getItem("token") === DEV_TOKEN) localStorage.removeItem("token");
    return;
  }

  // Pre-authenticate so the login screen is skipped.
  localStorage.setItem("token", DEV_TOKEN);

  // Per-request adapter override: return fixtures for known endpoints, leave
  // anything else to the normal network adapter.
  axios.interceptors.request.use((config) => {
    const body = buildMockBody(config);
    if (body) {
      config.adapter = () =>
        Promise.resolve({
          data: body,
          status: 200,
          statusText: "OK",
          headers: {},
          config,
          request: {},
        });
    }
    return config;
  });

  // eslint-disable-next-line no-console
  console.info("%c[DEV MOCK] active — OTP skipped, API mocked", "color:#15497c;font-weight:bold");
}
